"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, TrendingUp, TrendingDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { PriceAlert } from "@/lib/priceAlerts"

const APP_ID = process.env.NEXT_PUBLIC_INSTANTDB_APP_ID || ""
const IS_MOCK_MODE = !APP_ID || APP_ID === "placeholder_app_id"

export function PriceAlertNotification() {
  const [visibleAlerts, setVisibleAlerts] = useState<PriceAlert[]>([])

  // In mock mode, don't render anything
  if (IS_MOCK_MODE) {
    return null
  }

  const handleDismiss = (id: string) => {
    setVisibleAlerts(prev => prev.filter(alert => alert.id !== id))
  }

  // Only render if there are visible alerts
  if (visibleAlerts.length === 0) {
    return null
  }

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md px-4 pointer-events-none">
      <AnimatePresence>
        {visibleAlerts.map((alert) => {
          const isAbove = alert.direction === 'above'
          return (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, y: -50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={cn(
                "relative p-4 rounded-lg shadow-lg mb-3 flex items-start gap-3 pointer-events-auto",
                isAbove
                  ? "bg-green-600 text-white"
                  : "bg-red-600 text-white"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                isAbove ? "bg-green-700/50" : "bg-red-700/50"
              )}>
                {isAbove ? (
                  <TrendingUp className="w-5 h-5" />
                ) : (
                  <TrendingDown className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">Price Alert Triggered!</h3>
                <p className="text-sm">
                  <span className="font-semibold">{alert.symbol}</span> has gone {alert.direction} your target price of ${alert.targetPrice.toFixed(2)}.
                  {alert.currentPrice && (
                    <> Current price: ${alert.currentPrice.toFixed(2)}.</>
                  )}
                </p>
                {alert.customMessage && (
                  <p className="text-xs italic mt-2">&ldquo;{alert.customMessage}&rdquo;</p>
                )}
              </div>
              <button
                onClick={() => handleDismiss(alert.id)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
